import React, {
 useEffect, useState
} from 'react';
import {
 Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
 conversationService
} from 'shared/api';
import {
 useParams
} from 'react-router-dom';
import type {
 ConversationSession, ConversationMessage
} from 'shared/api/services/bexService.types';
import {
 Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from 'shared/components/ui/table';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Loader2, MessageSquare, Clock, Users, Activity
} from 'lucide-react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Dialog, DialogContent, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
 ScrollArea
} from 'shared/components/ui/scroll-area';

interface ConversationHistoryTabProps {
    channelAddress: string;
}

export const ConversationHistoryTab: React.FC<ConversationHistoryTabProps> = ({
 channelAddress
}) => {
    const {
 oragID, subOragID
} = useParams();
    const [sessions, setSessions] = useState<ConversationSession[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalConversations, setTotalConversations] = useState(0);
    const [totalUsers, setTotalUsers] = useState(0);
    const [activeSessions, setActiveSessions] = useState(0);
    const [selectedSession, setSelectedSession] = useState<ConversationSession | null>(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);

    // Map of conversationSessionId → userId (from tracking sessions)

    useEffect(() => {
        if (!oragID || !subOragID || !channelAddress) return;

        setLoading(true);

        const sessionsPromise = conversationService.listSessions(oragID, subOragID, channelAddress);
        const conversationsPromise = conversationService.listConversations(oragID, subOragID, channelAddress);
        const usersPromise = conversationService.listUsers(oragID, subOragID);
        const trackingPromise = conversationService.listTrackingSessions(oragID, subOragID, channelAddress);

        Promise.allSettled([sessionsPromise, conversationsPromise, usersPromise, trackingPromise])
            .then(([sessionsResult, conversationsResult, usersResult, trackingResult]) => {
                if (sessionsResult.status === 'fulfilled') {
                    setSessions(sessionsResult.value.data || []);
                }
                if (conversationsResult.status === 'fulfilled') {
                    setTotalConversations((conversationsResult.value.data || []).length);
                }
                if (usersResult.status === 'fulfilled') {
                    setTotalUsers((usersResult.value.data || []).length);
                }
                // Track active sessions count & build sessionId → userId lookup
                if (trackingResult.status === 'fulfilled') {
                    const trackingData = trackingResult.value.data || [];
                    setActiveSessions(trackingData.length);
                }
            })
            .finally(() => setLoading(false));
    }, [oragID, subOragID, channelAddress]);

    const handleViewMessages = (session: ConversationSession) => {
        setSelectedSession(session);
        setDetailDialogOpen(true);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-background/60 backdrop-blur-xl border-white/10">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <MessageSquare className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Conversations</p>
                                <p className="text-2xl font-bold">{totalConversations}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-background/60 backdrop-blur-xl border-white/10">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-blue-500/10">
                                <Activity className="w-5 h-5 text-blue-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Sessions</p>
                                <p className="text-2xl font-bold">{sessions.length}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-background/60 backdrop-blur-xl border-white/10">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-amber-500/10">
                                <Activity className="w-5 h-5 text-amber-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Active Sessions</p>
                                <p className="text-2xl font-bold">{activeSessions}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-background/60 backdrop-blur-xl border-white/10">
                    <CardContent className="pt-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-500/10">
                                <Users className="w-5 h-5 text-green-500" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Total Users</p>
                                <p className="text-2xl font-bold">{totalUsers}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Sessions Table */}
            <Card className="bg-background/60 backdrop-blur-xl border-white/10 shadow-2xl overflow-hidden">
                <CardHeader>
                    <CardTitle className="text-xl font-bold flex items-center gap-2">
                        <MessageSquare className="w-5 h-5 text-primary" />
                        Conversation Sessions
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="rounded-md border border-white/5">
                        <Table>
                            <TableHeader className="bg-white/5">
                                <TableRow>
                                    <TableHead>Session ID</TableHead>
                                    <TableHead>Messages</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead>Updated</TableHead>
                                    <TableHead className="text-right">Action</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sessions.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                                            No sessions found for this channel.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sessions.map((session, idx) => (
                                        <TableRow key={session.conversationSessionId || idx} className="hover:bg-white/5 transition-colors">
                                            <TableCell>
                                                <span className="text-xs font-mono text-muted-foreground truncate max-w-[140px] block">
                                                    {session.conversationSessionId || '—'}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className="min-w-[60px] justify-center">
                                                    {session.messages?.length || 0} msgs
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Clock className="w-3 h-3" />
                                                    {session.createdAt
                                                        ? new Date(session.createdAt).toLocaleString()
                                                        : '—'}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Clock className="w-3 h-3" />
                                                    {session.updatedAt
                                                        ? new Date(session.updatedAt).toLocaleString()
                                                        : '—'}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleViewMessages(session)}
                                                    disabled={!session.messages?.length}
                                                >
                                                    View Messages
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Messages Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="max-w-2xl bg-background/95 backdrop-blur-2xl border-white/10">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <MessageSquare className="w-5 h-5 text-primary" />
                            Messages — {selectedSession?.conversationSessionId?.slice(0, 12)}...
                        </DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="h-[500px] w-full pr-4 mt-4">
                        {selectedSession?.messages?.length ? (
                            <div className="space-y-4">
                                {selectedSession.messages.map((msg: ConversationMessage, idx: number) => {
                                    const isUser = msg.direction === 'in';
                                    return (
                                        <div
                                            key={idx}
                                            className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div className="max-w-[80%]">
                                                {/* Role label */}
                                                <div className={`flex items-center gap-1 mb-1 text-[11px] font-semibold tracking-wide uppercase opacity-60 ${isUser ? 'justify-end' : 'justify-start'}`}>
                                                    {isUser ? 'User' : 'AI Assistant'}
                                                </div>
                                                {/* Message bubble */}
                                                <div
                                                    className={`p-3 rounded-xl border ${
                                                        isUser
                                                            ? 'bg-white/10 border-white/20 rounded-tr-sm'
                                                            : 'bg-white/5 border-white/10 rounded-tl-sm'
                                                    }`}
                                                >
                                                    <div className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</div>
                                                    <div className="text-[10px] opacity-40 mt-2 text-right">
                                                        {msg.timestamp && new Date(msg.timestamp).toLocaleTimeString()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center text-muted-foreground py-8">No messages in this session.</div>
                        )}
                    </ScrollArea>
                </DialogContent>
            </Dialog>
        </div>
    );
};
